import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject,
} from '@angular/core';
import {
    AbstractControl,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { LookupApiService } from '@wdx/clmi/api-services/services';
import {
    AppStatus,
    CodedReference,
    DocumentRequirementType,
    LookupFieldResult,
    LookupSourceType,
    PartyType,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import {
    PartyRoleType,
    Regarding,
    TRANSLATION_ADD_BTN,
    TRANSLATION_CANCEL_BTN,
    TRANSLATION_DOC_REQ_SELECT_ORG,
    TRANSLATION_DOC_REQ_SELECT_PERSON,
    TRANSLATION_DOC_REQ_SELECT_PERSON_ORG,
    TRANSLATION_DOC_REQ_SELECT_PRODUCT,
    TRANSLATION_LEGEND_SELECT_DOC_REQUIREMENT,
    TRANSLATION_MODAL_TITLE_ADD_DOC_REQUIREMENT,
    TRANSLATION_PLACEHOLDER_SELECT,
    TRANSLATION_TEXT_NO_RECORD_ASSOCIATED,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import * as R from 'ramda';
import { BehaviorSubject, Observable, filter, map, takeUntil } from 'rxjs';
import { FeatureSvg } from '../../../../models/svg.model';
import * as documentChecklistActions from '../../../../state/document-checklist/document-checklist.actions';
import { DocumentsFacadeService } from '../shared/services';
import { IAddDocumentSecondary } from './add-document-requirement-modal.interface';
import { SECONDARY_SELECT } from './add-document-requirement-modal.static';

@Component({
    templateUrl: './add-document-requirement-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AddDocumentRequirementModalComponent
    extends WdxDestroyClass
    implements OnInit
{
    @Input() caseId: string;
    @Input() entityId: string;
    @Input() regarding?: Regarding[];
    @Input() systemEntity: SystemEntity;
    @Input() app: AppStatus;
    @Input() includeCaseEntity = false;

    @Output() cancelled = new EventEmitter();
    @Output() documentTypeAdded = new EventEmitter();

    form: UntypedFormGroup;

    relatedParty$ = new BehaviorSubject<LookupFieldResult[]>(null);
    relatedPartyIsLoading$ = new BehaviorSubject<boolean>(true);
    isSaving$ = new BehaviorSubject<boolean>(null);
    secondarySelect$ = new BehaviorSubject<IAddDocumentSecondary>(null);
    noRecordsForType$ = new BehaviorSubject<string>(null);
    documentRequirementTypes$: Observable<DocumentRequirementType[]>;

    readonly FEATURE_SVG = FeatureSvg;
    readonly TITLE_ADD_DOC_REQUIREMENT =
        TRANSLATION_MODAL_TITLE_ADD_DOC_REQUIREMENT;
    readonly ADD_BTN = TRANSLATION_ADD_BTN;
    readonly CANCEL_BTN = TRANSLATION_CANCEL_BTN;
    readonly PLACEHOLDER_SELECT = TRANSLATION_PLACEHOLDER_SELECT;
    readonly NO_RECORD_ASSOCIATED = TRANSLATION_TEXT_NO_RECORD_ASSOCIATED;
    readonly LEGEND_SELECT_DOC_REQUIREMENT =
        TRANSLATION_LEGEND_SELECT_DOC_REQUIREMENT;

    displayRelatedPartyControl = false;

    private actionsSubject$ = inject(ActionsSubject);
    private documentsFacadeService = inject(DocumentsFacadeService);
    private lookupsService = inject(LookupApiService);

    get documentRequirementControl(): AbstractControl<CodedReference> {
        return this.form.get('documentRequirement');
    }

    get relatedPartyControl(): AbstractControl {
        return this.form.get('relatedParty');
    }

    ngOnInit(): void {
        this.form = new UntypedFormGroup({
            documentRequirement: new UntypedFormControl(
                null,
                Validators.required,
            ),
            relatedParty: new UntypedFormControl(null, Validators.required),
        });

        this.documentRequirementTypes$ = this.documentsFacadeService
            .getDocumentRequirementTypes$(this.app)
            .pipe(
                filter((documentRequirementTypes) =>
                    Boolean(documentRequirementTypes),
                ),
            );

        this.subscribeToActions();

        this.subscribeToValueChanges();
    }

    subscribeToValueChanges(): void {
        this.documentRequirementControl.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((documentRequirementType) => {
                this.resetObservablesData();

                if (SECONDARY_SELECT.includes(documentRequirementType?.type)) {
                    this.displayRelatedPartyControl = true;
                    this.relatedPartyControl.setValidators(Validators.required);

                    if (
                        documentRequirementType.type ===
                        SystemEntity.ClientProduct
                    ) {
                        this.onClientProductRequirementValueChange();
                    }

                    if (documentRequirementType.type === SystemEntity.Person) {
                        this.onPersonRequirementValueChange();
                    }

                    if (
                        documentRequirementType.type ===
                        SystemEntity.Organisation
                    ) {
                        this.onOrganisationRequirementValueChange();
                    }

                    if (documentRequirementType.type === SystemEntity.Party) {
                        this.onPartyRequirementValueChange();
                    }
                } else {
                    this.onNoSecondarySelectRequirementValueChange();
                }

                this.relatedPartyControl.updateValueAndValidity();
            });
    }

    subscribeToActions(): void {
        this.actionsSubject$
            .pipe(
                takeUntil(this.destroyed$),
                ofType(documentChecklistActions.addDocumentRequirementSuccess),
            )
            .subscribe(() => {
                this.isSaving$.next(false);
                this.documentTypeAdded.emit();
            });

        this.actionsSubject$
            .pipe(
                takeUntil(this.destroyed$),
                ofType(documentChecklistActions.addDocumentRequirementError),
            )
            .subscribe(() => {
                this.isSaving$.next(false);
            });
    }

    resetObservablesData(): void {
        this.relatedParty$.next(null);
        this.relatedPartyControl.setValue(null);
        this.relatedPartyIsLoading$.next(true);
        this.noRecordsForType$.next(null);
    }

    onNoSecondarySelectRequirementValueChange(): void {
        this.displayRelatedPartyControl = false;
        this.relatedPartyControl.removeValidators(Validators.required);
    }

    onClientProductRequirementValueChange(): void {
        this.secondarySelect$.next({
            label: TRANSLATION_DOC_REQ_SELECT_PRODUCT,
        });
        this.getLookUpData(
            LookupSourceType.ClientProduct,
            'ENUM_SYSTEMENTITY_CLIENTPRODUCT',
        );
    }

    onOrganisationRequirementValueChange(): void {
        this.secondarySelect$.next({ label: TRANSLATION_DOC_REQ_SELECT_ORG });
        this.getLookUpData(
            LookupSourceType.Organisation,
            'ENUM_SYSTEMENTITY_ORGANISATION',
        );
    }

    onPersonRequirementValueChange(): void {
        this.secondarySelect$.next({
            label: TRANSLATION_DOC_REQ_SELECT_PERSON,
        });
        this.getLookUpData(
            LookupSourceType.RelatedParty,
            'ENUM_SYSTEMENTITY_PERSON',
            PartyType.Person,
        );
    }

    onPartyRequirementValueChange(): void {
        this.secondarySelect$.next({
            label: TRANSLATION_DOC_REQ_SELECT_PERSON_ORG,
        });
        this.getLookUpData(
            LookupSourceType.RelatedParty,
            'ENUM_SYSTEMENTITY_RELATEDPARTY',
        );
    }

    getLookUpData(
        endpoint: string,
        noRecordText: string,
        includeOnly?: PartyType,
    ): void {
        this.lookupsService
            .getContextualLookup(
                endpoint,
                this.regarding?.map(({ id, type }) => ({
                    lookupType: type as unknown as LookupSourceType,
                    id,
                })) || [],
            )
            .pipe(
                map((relatedParties) =>
                    includeOnly
                        ? relatedParties.filter(
                              (x) => x.partyType === includeOnly,
                          )
                        : relatedParties,
                ),
            )
            .subscribe((relatedParties) => {
                if (!relatedParties?.length) {
                    this.noRecordsForType$.next(noRecordText);
                }
                this.relatedParty$.next(relatedParties);
                this.relatedPartyIsLoading$.next(false);
            });
    }

    onSubmit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        this.isSaving$.next(true);

        const documentRequirementType = this.documentRequirementControl.value;
        const relatedPartyId = this.relatedPartyControl.value;

        const relatedParty = this.relatedParty$.value?.find(
            (relatedParty) => relatedParty.id === relatedPartyId,
        );
        const regarding = this.regarding?.[0];

        // If the entity is one that is included in PartyType, then set it to Party; return input entity otherwise
        const setPartyEntity = (entity: SystemEntity) =>
            PartyType[entity] ? SystemEntity.Party : entity;

        const entity = setPartyEntity(this.systemEntity);

        // If the entity is a Party, then we need to search the app object for any of the potential party types, so create
        // an array of party types + Party itself to search for; single item array of the entity otherwise
        const entityTypeList =
            this.systemEntity === SystemEntity.Party
                ? [...R.values(PartyType), SystemEntity.Party]
                : [this.systemEntity];

        // Find the entity in the app using the type param
        const entityInApp = this.app
            ? Object.values(this.app).find(
                  (prop) => prop.type && entityTypeList.includes(prop.type),
              )
            : null;

        // Reduce the entity down to only the params required
        const entityRef =
            (entityInApp &&
                (R.pick(
                    ['id', 'name', 'type'],
                    entityInApp,
                ) as CodedReference)) ||
            (this.entityId
                ? ({
                      id: this.entityId,
                      type: this.systemEntity,
                  } as CodedReference)
                : null);

        let relatedEntityType = relatedParty?.lookupType
            ? (relatedParty.lookupType as unknown as SystemEntity)
            : regarding?.type;

        if (
            documentRequirementType.type === SystemEntity.ClientProduct &&
            relatedEntityType === SystemEntity.Portfolio
        ) {
            relatedEntityType = SystemEntity.ClientProduct;
        }

        let relatedEntity = relatedParty
            ? setPartyEntity(relatedEntityType)
            : regarding?.type;

        let relatedEntityRef = relatedParty
            ? {
                  id: relatedParty.id,
                  name: relatedParty.name,
                  type: relatedEntityType,
              }
            : regarding
              ? { id: regarding.id, name: regarding.name, type: regarding.type }
              : null;

        // In the case that this is called on a partyRole eg. client, the api does not want both 'Client' and 'PartyRole' being sent
        // so we need to filter out PartyRole here
        if (PartyRoleType[entity] && relatedEntity === SystemEntity.PartyRole) {
            relatedEntity = null;
            relatedEntityRef = null;
        }

        const caseRef =
            this.includeCaseEntity &&
            entity !== SystemEntity.Case &&
            (this.app?.case?.id || this.caseId)
                ? ({
                      id: this.app?.case?.id || this.caseId,
                      name: this.app?.case?.name,
                      type: SystemEntity.Case,
                  } as CodedReference)
                : null;

        const additionalEntities = caseRef
            ? [
                  {
                      entity: SystemEntity.Case,
                      entityRef: caseRef,
                  },
                  ...(relatedParty && regarding
                      ? [
                            {
                                entity: regarding.type,
                                entityRef: {
                                    id: regarding.id,
                                    name: regarding.name,
                                    type: regarding.type,
                                } as CodedReference,
                            },
                        ]
                      : []),
              ]
            : null;

        if (additionalEntities) {
            this.documentsFacadeService.addDocumentRequirement(
                documentRequirementType,
                entity,
                entityRef,
                relatedEntity,
                relatedEntityRef,
                additionalEntities,
            );
            return;
        }

        this.documentsFacadeService.addDocumentRequirement(
            documentRequirementType,
            entity,
            entityRef,
            relatedEntity,
            relatedEntityRef,
        );
    }

    onCancel(): void {
        this.cancelled.emit();
    }
}
